import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createLocalGame } from "../../game/game-factory";
import GameView from "../../../core/engine/views/game-view";
import Settings from "../Settings";
import TouchControls from "../TouchControls";
import { gameTypeToPreset, applyGamePreset } from "../../../core/engine/game-presets";
import { updateHighScores } from "../../utils/highScores";
import ResultScreen from "../ResultScreen";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import KeyboardInputManager, { loadConfig } from "../../managers/keyboard-input-manager";
import TouchInputManager from "../../managers/touch-input-manager";
import { CS_StatType } from "../../../core/engine/models/stat";
import Game from "../../../core/engine/game";
import UnifiedInputManager from "../../managers/unified-input-manager";
import "./SinglePlayerGame.css";
import { ScorePane } from "./ScorePane";

interface LocationState {
  gameType?: "normal" | "sprint";
}

// Detect if device is mobile/touch-enabled
const isMobileDevice = (): boolean => {
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 2) ||
    window.matchMedia("(pointer: coarse)").matches
  );
};

export function SinglePlayerGame() {
  const navigate = useNavigate();
  const location = useLocation();
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [lines, setLines] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [highScoreRank, setHighScoreRank] = useState(-1);
  const [gameEnded, setGameEnded] = useState(false);
  const [gameStatsSnapshot, setGameStatsSnapshot] = useState<Record<string, { getValue: () => number }> | null>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<Game | null>(null);
  const touchInputRef = useRef<TouchInputManager | null>(null);
  const unifiedInputRef = useRef<UnifiedInputManager | null>(null);
  const [savedPlayerName] = useLocalStorage("playerName", "Player") as [string, (value: string) => void];

  // Get game type from navigation state (default to "normal")
  const gameType = (location.state as LocationState | null)?.gameType || "normal";

  useEffect(() => {
    const mobile = isMobileDevice();
    setIsMobile(mobile);

    const calculateCanvasSize = (): number => {
      if (!canvasContainerRef.current) return 800;
      const container = canvasContainerRef.current;
      const containerHeight = container.clientHeight;
      const containerWidth = container.clientWidth;

      // Calculate max width based on height
      // GameView aspect ratio is approximately Height = 1.3 * Width
      // So Width = Height / 1.3
      const maxWidthFromHeight = containerHeight / 1.3;
      
      // Fit within container width
      const canvasWidth = Math.min(containerWidth, maxWidthFromHeight);
      
      // Enforce min render width of 800 for quality
      return Math.max(800, canvasWidth);
    };

    // Create input sources
    const keyboardInput = new KeyboardInputManager(document, loadConfig(), true);
    const touchInput = new TouchInputManager();
    const inputSources = [keyboardInput, touchInput];

    const { game, unifiedInput } = createLocalGame(
      undefined,
      undefined,
      undefined,
      inputSources
    );
    gameRef.current = game;
    touchInputRef.current = touchInput;
    unifiedInputRef.current = unifiedInput;
    let view: GameView | null = null;
    let isRunning = true;
    let resizeObserver: ResizeObserver | null = null;

    const initView = () => {
      let canvasSize = calculateCanvasSize();
      const canvasElement = document.getElementById("canvas") as HTMLCanvasElement;
      if (!canvasElement) return;
      
      // Enforce min render width of 800 (results in ~480px board width)
      view = new GameView(canvasElement, game, Math.max(canvasSize, 800));

      if (canvasContainerRef.current) {
        // No additional resizing needed - calculateCanvasSize respects container dimensions
      }

      let lastUpdateTime = 0;
      const updateInterval = 1000 / 30;
      
      // Time accumulation for game logic (100fps = 10ms per step)
      let lastGameStepTime = performance.now();
      let gameAccumulator = 0;

      const loop = (frameTime: number) => {
        if (!isRunning) return;

        // Update unified input manager
        if (unifiedInputRef.current) {
          unifiedInputRef.current.update();
        }

        // Game logic at 100fps (every 10ms) - matches multiplayer loop
        const currentTime = performance.now();
        const elapsed = currentTime - lastGameStepTime;
        lastGameStepTime = currentTime;
        gameAccumulator += elapsed;

        // Cap accumulator to prevent excessive catch-up (matches original quadra.cc:497-500)
        if (gameAccumulator > 300) {
          const overmind = game.overmind;
          if (overmind) {
            overmind.framecount += Math.floor((gameAccumulator - 300) / 10);
          }
          gameAccumulator = 300;
        }

        // Step game logic at 100fps
        while (gameAccumulator >= 10) {
          gameAccumulator -= 10;
          game.step();
        }

        // Render
        if (view) {
          view.draw(frameTime);
        }

        // Update stats at 30fps
        const deltaTime = frameTime - lastUpdateTime;
        if (deltaTime >= updateInterval) {
          setScore(game.stats["SCORE"].getValue());
          setLevel(game.canvas.level);
          setLines(game.stats["LINESTOT"].getValue());
          lastUpdateTime = frameTime;
        }

        requestAnimationFrame(loop);
      };
      loop(0);
      
      // Apply single player preset configuration
      // Reference: quadra/source/multi_player.cc:356-371 (Single_player_iterate::init)
      const preset = gameTypeToPreset(gameType);
      const presetConfig = applyGamePreset(preset);
      
      // Set single player mode on game
      if (presetConfig.single) {
        // Set up game end callback for high score checking
        // Reference: quadra/source/multi_player.cc:176-184
        game.onSinglePlayerGameEnd = (score: number, level: number, lines: number) => {
          // Check high score
          // Reference: quadra/source/highscores.cc:128-179 (Highscores::update)
          const playerName = savedPlayerName || "Player";
          const rank = updateHighScores(score, level, lines, playerName);
          setHighScoreRank(rank);
          
          // Snapshot game stats for ResultScreen
          if (game.stats) {
            // Create a snapshot of all stats with getValue methods
            const statsSnapshot: Record<string, { getValue: () => number }> = {};
            Object.keys(game.stats).forEach((key) => {
              const stat = game.stats[key as CS_StatType];
              if (stat && typeof stat.getValue === 'function') {
                statsSnapshot[key] = {
                  getValue: () => stat.getValue()
                };
              }
            });
            setGameStatsSnapshot(statsSnapshot);
          }
          
          setGameEnded(true); // Mark game as ended to show result screen
        };
        
        // Apply preset configuration to game
        // This will be handled in game.start() with parameters
        game.start({
          single: presetConfig.single,
          network: presetConfig.network,
          levelUp: presetConfig.levelUp,
          gameEnd: presetConfig.gameEnd,
          gameEndValue: presetConfig.gameEndValue,
        });
      } else {
        game.start();
      }
    };

    let resizePending = false;
    let resizeRAFId: number | null = null;
    
    const handleResize = () => {
      if (resizePending || !view || !canvasContainerRef.current) return;
      
      resizePending = true;
      
      // Cancel any pending resize
      if (resizeRAFId !== null) {
        cancelAnimationFrame(resizeRAFId);
      }
      
      resizeRAFId = requestAnimationFrame(() => {
        resizePending = false;
        resizeRAFId = null;
        if (view && canvasContainerRef.current) {
          const newSize = calculateCanvasSize();
          view.resize(Math.max(newSize, 800));
        }
      });
    };

    const timeoutId = setTimeout(() => {
      initView();

      if (canvasContainerRef.current) {
        resizeObserver = new ResizeObserver(() => {
          // Use requestAnimationFrame to defer resize handling
          // This prevents ResizeObserver loop warnings
          handleResize();
        });
        resizeObserver.observe(canvasContainerRef.current);
      }
    }, 50);

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timeoutId);
      if (resizeRAFId !== null) {
        cancelAnimationFrame(resizeRAFId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener("resize", handleResize);
      isRunning = false;
      game.stop();
    };
  }, [gameType, savedPlayerName]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (showSettings) {
          setShowSettings(false);
        } else {
          setShowMenu((prev) => !prev);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showSettings]);

  return (
    <div className="game-screen">
      {/* Canvas background element (replaces .game-screen::before) */}
      <div
        className="canvas-background"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      {showSettings && (
        <div className="game-menu-overlay">
          <Settings
            onClose={() => setShowSettings(false)}
            showCloseButton={true}
          />
        </div>
      )}
      {showMenu && !showSettings && (
        <div className="game-menu-overlay" onClick={() => setShowMenu(false)}>
          <div className="game-menu" onClick={(e) => e.stopPropagation()}>
            <h2>Game Menu</h2>
            <button className="menu-button" onClick={() => navigate("/")}>
              Exit to Main Menu
            </button>
            <button
              className="menu-button"
              onClick={() => {
                setShowMenu(false);
                setShowSettings(true);
              }}
            >
              Options
            </button>
            <button className="menu-button" onClick={() => setShowMenu(false)}>
              Resume Game
            </button>
          </div>
        </div>
      )}
      <div className="single-player-stage">
        <div className="single-player-score-section">
          <ScorePane score={score} level={level} lines={lines} />
        </div>
        <div className="single-player-game-section">
          <div className="canvas-container" ref={canvasContainerRef}>
            <canvas id="canvas"></canvas>
          </div>
        </div>
      </div>
      {isMobile && touchInputRef.current && (
        <TouchControls touchInput={touchInputRef.current} />
      )}
      {/* Result Screen - Show when single player game ends (shows stats, with high score tab if applicable) */}
      {gameEnded && (
        <ResultScreen
          gameState={null}
          localStats={{ score, level, lines }}
          isSinglePlayer={true}
          gameStats={gameStatsSnapshot}
          highScoreRank={highScoreRank}
          onQuit={() => {
            setGameEnded(false);
            setHighScoreRank(-1);
            setGameStatsSnapshot(null);
            navigate("/");
          }}
          onRestart={() => {
            setGameEnded(false);
            setHighScoreRank(-1);
            setGameStatsSnapshot(null);
            navigate("/single-player/settings");
          }}
        />
      )}
    </div>
  );
}

